/**
 * Admin Readers Page
 *
 * Page for managing Beyond Readers books (Director only).
 * Lets the director add/edit/delete books (swap the book of the month, change
 * cover/description) by pasting links — no developer needed.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { Spinner } from "../../components/ui";
import {
  useBooks,
  useCreateBook,
  useDeleteBook,
  useImportDefaultBooks,
  useUpdateBook,
} from "../../hooks/useReaders";
import type { Book, CreateBookRequest } from "../../types/material";
import { BOOKS_2026 } from "../readers/ReadersPage";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CURRENT_YEAR = 2026;

const emptyForm: CreateBookRequest = {
  title: "",
  author: "",
  description: "",
  tagline: "",
  pages: 0,
  coverUrl: "",
  amazonUrl: "",
  meetLink: "",
  month: 0,
  year: CURRENT_YEAR,
  durationMonths: 1,
};

export function AdminReadersPage() {
  const { data: books, isLoading } = useBooks(CURRENT_YEAR);
  const createBook = useCreateBook();
  const updateBook = useUpdateBook();
  const deleteBook = useDeleteBook();
  const importBooks = useImportDefaultBooks();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selected, setSelected] = useState<Book | null>(null);
  const [form, setForm] = useState<CreateBookRequest>(emptyForm);
  const [toDelete, setToDelete] = useState<Book | null>(null);

  const openCreate = () => {
    setForm(emptyForm);
    setIsEditing(false);
    setSelected(null);
    setIsModalOpen(true);
  };

  const openEdit = (book: Book) => {
    setForm({
      title: book.title,
      author: book.author,
      description: book.description,
      tagline: book.tagline,
      pages: book.pages,
      coverUrl: book.coverUrl,
      amazonUrl: book.amazonUrl,
      meetLink: book.meetLink,
      month: book.month,
      year: book.year,
      durationMonths: book.durationMonths,
    });
    setSelected(book);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelected(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditing && selected) {
      await updateBook.mutateAsync({ id: selected.id, data: form });
    } else {
      await createBook.mutateAsync(form);
    }
    closeModal();
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    await deleteBook.mutateAsync(toDelete.id);
    setToDelete(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-earth-700 hover:text-earth-700 text-sm mb-2 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-display font-bold text-earth-800">
                Beyond Readers
              </h1>
              <p className="text-earth-700 text-sm mt-1">
                Manage the book club books for {CURRENT_YEAR}
              </p>
            </div>
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <i className="ri-add-line"></i>
              New Book
            </button>
          </div>
        </div>

        {/* List */}
        <div className="bg-white rounded-xl border border-earth-200 overflow-hidden">
          <div className="divide-y divide-earth-100">
            {books?.map((book) => (
              <div
                key={book.id}
                className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 hover:bg-earth-50/50"
              >
                <div className="w-12 h-16 bg-earth-100 rounded overflow-hidden flex-shrink-0">
                  {book.coverUrl ? (
                    <img
                      src={book.coverUrl}
                      alt={book.title}
                      className="w-full h-full object-cover"
                    />
                  ) : null}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-medium text-rose-500 uppercase tracking-wide">
                    {MONTHS[book.month]}
                    {book.durationMonths > 1
                      ? ` – ${MONTHS[book.month + book.durationMonths - 1] ?? ""}`
                      : ""}
                  </p>
                  <h3 className="font-medium text-earth-800 truncate">
                    {book.title}
                  </h3>
                  <p className="text-xs text-earth-700 truncate">{book.author}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => openEdit(book)}
                    className="p-1.5 hover:bg-earth-100 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <i className="ri-edit-line text-earth-500"></i>
                  </button>
                  <button
                    onClick={() => setToDelete(book)}
                    className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <i className="ri-delete-bin-line text-red-500"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
          {(books?.length ?? 0) === 0 && (
            <div className="text-center py-12 px-4">
              <div className="w-14 h-14 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-book-read-line text-2xl text-rose-500"></i>
              </div>
              <p className="text-earth-700 font-medium">
                Seus livros de {CURRENT_YEAR} ainda não foram importados
              </p>
              <p className="text-earth-700 text-sm mt-1 mb-5 max-w-md mx-auto">
                Importe os {BOOKS_2026.length} livros já cadastrados para
                editá-los ou removê-los — sem precisar refazer do zero.
              </p>
              <button
                onClick={() => importBooks.mutate(BOOKS_2026)}
                disabled={importBooks.isPending}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-rose-500 hover:bg-rose-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {importBooks.isPending ? (
                  <>
                    <Spinner size="sm" />
                    Importando...
                  </>
                ) : (
                  <>
                    <i className="ri-download-2-line"></i>
                    Importar livros de {CURRENT_YEAR}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={closeModal}></div>
          <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-earth-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-earth-800">
                {isEditing ? "Edit Book" : "New Book"}
              </h2>
              <button
                onClick={closeModal}
                className="p-1.5 hover:bg-earth-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl text-earth-500"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, title: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-500"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Author
                  </label>
                  <input
                    type="text"
                    value={form.author}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, author: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Pages
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={form.pages}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, pages: Number(e.target.value) }))
                    }
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Month
                  </label>
                  <select
                    value={form.month}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, month: Number(e.target.value) }))
                    }
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-500"
                  >
                    {MONTHS.map((m, i) => (
                      <option key={i} value={i}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Year
                  </label>
                  <input
                    type="number"
                    value={form.year}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, year: Number(e.target.value) }))
                    }
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Cover image link
                </label>
                <input
                  type="url"
                  value={form.coverUrl}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, coverUrl: e.target.value }))
                  }
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Amazon link
                </label>
                <input
                  type="url"
                  value={form.amazonUrl}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, amazonUrl: e.target.value }))
                  }
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Tagline
                </label>
                <input
                  type="text"
                  value={form.tagline}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, tagline: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, description: e.target.value }))
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-500 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Duração (meses)
                </label>
                <input
                  type="number"
                  min={1}
                  max={12}
                  value={form.durationMonths}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      durationMonths: Math.max(1, Number(e.target.value) || 1),
                    }))
                  }
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-500"
                />
                <p className="text-[11px] text-earth-700 mt-1">
                  Quantos meses seguidos este livro cobre (ex.: 2 = o mês
                  escolhido e o seguinte, automaticamente).
                </p>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button
                  type="submit"
                  disabled={createBook.isPending || updateBook.isPending}
                  className="flex-1 px-4 py-2.5 bg-rose-500 hover:bg-rose-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {createBook.isPending || updateBook.isPending
                    ? "Saving..."
                    : isEditing
                      ? "Save Changes"
                      : "Create Book"}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2.5 text-earth-600 hover:text-earth-800 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {toDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setToDelete(null)}
          ></div>
          <div className="relative w-full max-w-sm bg-white rounded-xl shadow-xl p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-xl text-red-600"></i>
            </div>
            <h3 className="text-lg font-semibold text-earth-800 mb-2">
              Delete Book?
            </h3>
            <p className="text-sm text-earth-700 mb-6">
              <strong>{toDelete.title}</strong>
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={handleDelete}
                disabled={deleteBook.isPending}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {deleteBook.isPending ? "Deleting..." : "Delete"}
              </button>
              <button
                onClick={() => setToDelete(null)}
                className="flex-1 px-4 py-2.5 border border-earth-200 text-earth-600 hover:bg-earth-50 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminReadersPage;
